import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link } from '../../lib/router';
import { AsyncButton, Badge, Card, cx, Empty, ErrorBox, Loading, PageHeader, useToast } from '../../ui/components';

interface Line {
  invoiceId: string; number: string | null; openCents: number; currency: string; stage: string; dueDate: string | null;
  daysOpen: number; personId: string | null; name: string; email: string | null; phone: string | null;
  membershipStatus: string | null; payUrl: string | null;
}

/** Everything still unpaid, oldest first, with the two things staff can do: collect again or call. */
export function DunningPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const list = useApi(() => api<Line[]>(`${T(gym.slug)}/admin/dunning`), []);
  const total = (list.data ?? []).reduce((sum, l) => sum + l.openCents, 0);

  if (list.error) return <div className="page"><ErrorBox error={list.error} onRetry={list.reload} /></div>;
  if (!list.data) return <Loading />;

  return (
    <div className="page">
      <PageHeader title={i18n.t('dunning.title')} subtitle={i18n.t('dunning.subtitle', { amount: i18n.money(total), count: list.data.length })} />
      {list.data.length === 0 ? <Empty icon="✓">{i18n.t('dunning.none')}</Empty> : list.data.map((l) => (
        <Card key={l.invoiceId} className={cx('risk', l.daysOpen > 21 ? 'risk-high' : l.daysOpen > 7 ? 'risk-medium' : 'risk-low')}>
          <div className="row between wrap">
            <div>
              {l.personId ? <Link to={`${gym.base}/admin/clients/${l.personId}`}><strong>{l.name}</strong></Link> : <strong>{l.name}</strong>}
              <div className="small muted">
                {[l.number, i18n.t('dunning.open_for', { days: l.daysOpen }), l.membershipStatus && i18n.option(l.membershipStatus)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="row gap-sm">
              <strong>{i18n.money(l.openCents)}</strong>
              <Badge value={l.stage} label={i18n.t(`dunning.stage_${l.stage}`)} />
              {l.phone && <a className="btn btn-ghost btn-sm" href={`tel:${l.phone}`}>{i18n.t('crm.call')}</a>}
              <AsyncButton size="sm" variant="secondary" onClick={async () => {
                const r = await api(`${T(gym.slug)}/admin/dunning/${l.invoiceId}/retry`, { body: {} });
                toast.ok(i18n.t(r.attempted ? 'dunning.retried' : 'dunning.no_auto'));
                list.reload();
              }}>{i18n.t('dunning.retry')}</AsyncButton>
            </div>
          </div>
        </Card>
      ))}
      <p className="small muted">{i18n.t('dunning.help')}</p>
    </div>
  );
}
